/**
 * Tablet mode (0.9.0): a giving page on a shared device at an event.
 *
 * After each gift the page counts down and returns to its start, so the next donor never sees the
 * last one's details; and a donor who walks away part-way is cleared after two minutes without a
 * touch. The checkout itself keeps nothing on the device in this mode (`Wizard#tablet`).
 */

const IDLE_SECONDS = 120;

/**
 * Starts the tablet behaviour for the page's tablet root.
 *
 * @param {HTMLElement} root The element carrying `data-anudan-tablet`.
 * @return {void}
 */
export function startTablet( root ) {
	const start = root.getAttribute( 'data-anudan-tablet-start' ) || window.location.href;
	const resetSeconds = Math.max( 5, parseInt( root.getAttribute( 'data-anudan-tablet-reset' ) || '20', 10 ) || 20 );
	const onForm = null !== root.querySelector( '[data-anudan-checkout]' );
	const goToStart = () => window.location.replace( start );
	let idle = null;
	let countdown = null;

	const restartIdle = () => {
		window.clearTimeout( idle );

		if ( onForm && null === countdown ) {
			idle = window.setTimeout( goToStart, IDLE_SECONDS * 1000 );
		}
	};

	[ 'pointerdown', 'keydown', 'input' ].forEach( ( event ) => document.addEventListener( event, restartIdle, true ) );
	restartIdle();

	if ( ! onForm ) {
		return;
	}

	const observer = new MutationObserver( () => {
		if ( null !== countdown || null === root.querySelector( '.anudan-checkout__confirmation' ) ) {
			return;
		}

		window.clearTimeout( idle );
		countdown = showCountdown( root, resetSeconds, goToStart );
	} );

	observer.observe( root, { childList: true, subtree: true } );
}

/**
 * Shows "Next donor in N seconds" with a button to start at once, and counts down.
 *
 * @param {HTMLElement} root    The tablet root.
 * @param {number}      seconds How long to wait.
 * @param {Function}    done    Called at zero.
 * @return {number} The interval id.
 */
function showCountdown( root, seconds, done ) {
	const i18n = window.wp && window.wp.i18n ? window.wp.i18n : null;
	const panel = document.createElement( 'div' );
	const text = document.createElement( 'p' );
	const button = document.createElement( 'button' );
	let left = seconds;

	// Q1 (0.9.40), the design's tablet thank-you: the button to start a new gift, then how long until the page does it itself.
	const sentence = () => ( i18n
		? i18n.__( 'This page returns to the start in {seconds} seconds.', 'anudan' )
		: 'This page returns to the start in {seconds} seconds.' ).replace( '{seconds}', String( left ) );

	panel.className = 'anudan-tablet__countdown';
	panel.setAttribute( 'role', 'status' );
	text.textContent = sentence();
	button.type = 'button';
	button.className = 'anudan-tablet__button';
	button.textContent = i18n ? i18n.__( 'Start a new gift', 'anudan' ) : 'Start a new gift';
	button.addEventListener( 'click', done );
	panel.appendChild( button );
	panel.appendChild( text );
	root.appendChild( panel );

	return window.setInterval( () => {
		left -= 1;

		if ( left <= 0 ) {
			done();

			return;
		}

		text.textContent = sentence();
	}, 1000 );
}
